import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Info, ShieldCheck, TriangleAlert } from 'lucide-react';
import { VerificationSummary } from '../../types/chat';
import { cn } from '../../lib/utils';

/**
 * Each level gets its own icon shape as well as its own colour, so the state
 * survives for anyone who cannot tell the two tints apart.
 */
const LEVELS = {
  ok: {
    labelKey: 'chat.verificationBasedOnDocuments',
    className: 'bg-success text-success-foreground',
    Icon: ShieldCheck,
  },
  caution: {
    labelKey: 'chat.verificationPartlyUnsupported',
    className: 'bg-warning text-warning-foreground',
    Icon: Info,
  },
  warning: {
    labelKey: 'chat.verificationNeedsReviewLabel',
    className: 'bg-destructive text-destructive-foreground',
    Icon: TriangleAlert,
  },
} as const;

// Prefix the severity in text too — colour alone is not an indicator (WCAG 1.4.1).
const FLAG_PREFIX: Record<string, string> = {
  warning: '⚠ ',
  caution: 'ℹ ',
};

interface ChatVerificationBadgeProps {
  summary: VerificationSummary;
  onToggle?: (open: boolean) => void;
}

const ClaimCount: React.FC<{ swatch: string; children: React.ReactNode }> = ({
  swatch,
  children,
}) => (
  <span className="inline-flex items-center gap-1 text-muted-foreground">
    <span className={cn('size-2.5 shrink-0 rounded-[2px]', swatch)} />
    {children}
  </span>
);

/** How well the answer is backed by sources, expandable to the detail. */
const ChatVerificationBadge: React.FC<ChatVerificationBadgeProps> = ({ summary, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const level = LEVELS[summary.level as keyof typeof LEVELS] ?? LEVELS.ok;
  // 'ok' flags are for the log, not the reader.
  const flags = summary.flags.filter((flag) => flag.severity !== 'ok');

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className={cn(
          'inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold',
          'transition-[filter,scale] hover:brightness-110 active:scale-[0.96]',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'motion-reduce:active:scale-100',
          level.className,
        )}
      >
        <level.Icon className="size-3.5" strokeWidth={2.2} />
        {t(level.labelKey)}
        <ChevronDown
          className={cn('size-4 transition-transform duration-200', open && 'rotate-180')}
          strokeWidth={2.2}
        />
      </button>

      {open && (
        <div className="mt-2 rounded-lg border bg-muted/40 p-3 text-xs animate-in fade-in-0 slide-in-from-top-1">
          <p className="font-semibold">{t('chat.verificationDetailsTitle')}</p>
          <p className="mb-2 text-muted-foreground">{t('chat.verificationDetailsDescription')}</p>

          <div className={cn('flex flex-wrap gap-4', flags.length > 0 && 'mb-2')}>
            <ClaimCount swatch="bg-success">
              {t('chat.verificationSupportedClaims', { count: summary.supported_claims })}
            </ClaimCount>
            <ClaimCount swatch="bg-warning">
              {t('chat.verificationUnsupportedClaims', { count: summary.uncited_claims })}
            </ClaimCount>
            {summary.weak_citations > 0 && (
              <span className="text-muted-foreground">
                {t('chat.verificationWeakCitations', { count: summary.weak_citations })}
              </span>
            )}
          </div>

          {flags.length > 0 && (
            <ul className="list-disc pl-4 leading-relaxed">
              {flags.map((flag, index) => (
                <li key={index}>
                  {FLAG_PREFIX[flag.severity] || ''}
                  {flag.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatVerificationBadge;
